import { useState } from 'react'
import { Avatar } from './bits'
import { isSystemId } from './users'

const ROLE_LABEL = { conductor: 'conductor', pasajero: 'pasajero' }

function messageTime(date) {
  if (!date) return null
  const d = new Date(date)
  if (isNaN(d.getTime())) return null
  return d.toLocaleString('es-AR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

function Bubble({ message, author }) {
  const time = messageTime(message.date)
  const isDriver = author.role === 'conductor'
  return (
    <div className='flex items-start gap-2'>
      <Avatar src={author.picture} name={author.name} size={26} />
      <div className='min-w-0 flex-1'>
        <p className='flex flex-wrap items-baseline gap-x-1.5'>
          <span className={`text-xs font-display font-bold ${isDriver ? 'text-coral_deep' : 'text-ink dark:text-gray-100'}`}>
            {author.name}
          </span>
          {author.role && (
            <span className='text-[10px] uppercase tracking-wide text-warm_gray dark:text-gray-400'>
              {ROLE_LABEL[author.role]}
            </span>
          )}
          {time && <span className='text-[10px] text-warm_gray dark:text-gray-400 tabular-nums'>{time}</span>}
        </p>
        <p className='text-sm text-ink/80 dark:text-gray-200 whitespace-pre-wrap break-words'>{message.text}</p>
      </div>
    </div>
  )
}

export default function ChatThread({ title, badge, avatar, channel, resolveAuthor, emptyLabel, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen)
  const [showSystem, setShowSystem] = useState(false)

  const messages = (channel && channel.messages) || []
  const userMessages = messages.filter(m => !isSystemId(m.user_id))
  const systemCount = messages.length - userMessages.length
  const visible = showSystem ? messages : userMessages
  const empty = userMessages.length === 0

  return (
    <div className={`rounded-2xl px-4 py-3 ${empty ? 'bg-coral/[0.08]' : 'bg-ink/[0.04] dark:bg-dk-raised'}`}>
      <button
        onClick={() => setOpen(o => !o)}
        className='flex w-full items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/30 rounded-lg'
      >
        {avatar && <Avatar src={avatar.picture} name={avatar.name} size={24} />}
        <span className='min-w-0 flex-1'>
          <span className='block font-display font-bold text-xs uppercase tracking-wide text-warm_gray dark:text-gray-400 truncate'>
            {title}
            {badge && (
              <span className='ml-1.5 normal-case tracking-normal rounded-full bg-amber/[0.18] text-amber_deep dark:bg-amber-500/10 dark:text-amber-400 px-1.5 py-0.5 text-[10px] font-medium'>
                {badge}
              </span>
            )}
          </span>
          <span className={`block text-sm font-medium ${empty ? 'text-coral_deep' : 'text-ink dark:text-gray-100'}`}>
            {empty
              ? emptyLabel
              : `${userMessages.length} ${userMessages.length === 1 ? 'mensaje' : 'mensajes'}`}
          </span>
        </span>
        <span className='text-warm_gray dark:text-gray-400 shrink-0' aria-hidden='true'>
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div className='mt-3 flex flex-col gap-3'>
          {visible.length === 0 ? (
            <p className='text-sm text-warm_gray dark:text-gray-400'>No hay mensajes para mostrar</p>
          ) : (
            visible.map((m, i) => (
              <Bubble key={m.id != null ? m.id : i} message={m} author={resolveAuthor(m.user_id, channel)} />
            ))
          )}
          {systemCount > 0 && (
            <button
              onClick={() => setShowSystem(s => !s)}
              className='self-start text-xs font-medium text-warm_gray dark:text-gray-400 hover:text-ink dark:hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/30 rounded'
            >
              {showSystem ? 'Ocultar' : 'Ver'} mensajes del sistema ({systemCount})
            </button>
          )}
        </div>
      )}
    </div>
  )
}
